import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    List,
    ListItem,
    Tabs,
    Theme,
    Typography,
} from '@mui/material';
import Tab from '@mui/material/Tab';
import { createStyles, makeStyles, withStyles } from '@mui/styles';
import { useOktaAuth } from '@okta/okta-react';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import Backend from '../backend/Backend';
import { ProcessingDetailLine, ValidationMessage } from '../common/types';
import AquantAlertIcon from '../resources/images/dialog-alert.svg';
import AquantCancelIcon from '../resources/images/dialog-cancel.svg';
import AquantDownloadIcon from '../resources/images/dialog-download.svg';
import AquantErrorIcon from '../resources/images/error.svg';
import { ConfigStore } from '../stores/ConfigStore';

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            width: '-webkit-fill-available',
        },
        dialogTitleContainer: {
            display: 'flex',
            alignItems: 'center',
            flex: 5,
        },
        dialogTitle: {
            flexGrow: 4,
        },
        cancelDialogImg: {
            marginLeft: 15,
            cursor: 'pointer',
        },
        dialogContentText: {
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.04em',
            color: '#4A4A4A',
        },
        tabsContainer: {
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
        },
        downloadBtn: {
            color: '#2468FF',
            fontSize: '13px',
            textTransform: 'capitalize',
            fontWeight: 500,
            padding: '0px',
        },
        cancelBtn: {
            padding: '5px 30px',
            fontWeight: '500',
            fontSize: '13px',
        },
        keepProcessingBtn: {
            padding: '5px 30px',
            background: '#2468FF',
            color: 'white',
            fontWeight: '400',
            fontSize: '13px',
            '&:hover': {
                background: '#2468FF',
                opacity: 0.7,
            },
        },
        downloadIcon: {
            fill: '#2468FF',
            marginRight: '7px',
        },
        tabs: {
            flex: 2,
            '& button': {
                minHeight: '0px',
                fontWeight: 400,
                padding: '10px 0px',
                marginRight: '30px',
                fontSize: '14px',
                letterSpacing: '0.04em',
                lineHeight: '24px',
                textTransform: 'capitalize',
                opacity: 1,
                color: '#616264',
            },
            '& button.Mui-selected': { color: '#616264' },
        },
        errorIcon: {
            fill: '#ED4D4D',
            fontSize: '15px',
        },
        mainSentence: {
            fontWeight: 500,
        },
        secondarySentence: {
            fontWeight: 400,
            marginTop: '4px',
        },
        listItem: {
            display: 'list-item',
            paddingLeft: '0px',
            fontSize: '13px',
            fontWeight: 400,
            letterSpacing: '0.1px',
        },
        listContainer: {
            listStyleType: 'disc',
            marginLeft: '14px',
        },
        tab: {
            fontWeight: 400,
            fontSize: '14px',
            letterSpacing: '0.04em',
        },
    }),
);

type FailedValidationDialogProps = {
    configStore: ConfigStore;
    validationProgress: any;
    handleDownload: (e: any, link: string) => void;
    taskId: string;
    setShowFailedValidationsDialog: (isOpen: boolean) => void;
    showFailedValidationsDialog: boolean;
    setIgnoreValidationsErrors: (ignoreErrors: boolean) => void;
};

export const DialogCard = withStyles((theme: Theme) =>
    createStyles({
        paper: {
            borderRadius: '14px',
            minWidth: '40%',
        },
    }),
)(Dialog);

interface TabPanelProps {
    children?: ReactElement | ReactElement[];
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
            {value === index && <div>{children}</div>}
        </div>
    );
}

function getTabProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

const FailedValidationsDialog = observer((props: FailedValidationDialogProps) => {
    const { showProgress } = props.configStore;
    const { authState, oktaAuth } = useOktaAuth();
    const [tabValue, setTabValue] = useState(0);
    const [lowImpactMessages, setLowImpactMessages] = useState<ValidationMessage[]>([]);
    const [highImpactMessages, setHighImpactMessages] = useState<ValidationMessage[]>([]);
    const classes = useStyles();
    const { task_id } = props.configStore;

    useEffect(() => {
        if (!props.validationProgress) {
            return;
        }

        const validationProgressMessages = props.validationProgress.messages.map((item) => JSON.parse(item));
        if (validationProgressMessages.find((msg) => msg.state === 'error')) {
            const highMessages = validationProgressMessages.filter(
                (msg) => msg.state === 'error' && msg.impact === 'high',
            );
            const lowMessages = validationProgressMessages.filter(
                (msg) => msg.state === 'error' && msg.impact === 'low',
            );
            setLowImpactMessages(lowMessages);
            setHighImpactMessages(highMessages);

            if (highMessages.length === 0) {
                setTabValue(1);
            }
        }
    }, [props.validationProgress]);

    const handleTabChange = (event: ChangeEvent<{}>, newTabValue: number) => {
        setTabValue(newTabValue);
    };

    const handleCloseDialog = () => {
        props.setShowFailedValidationsDialog(false);
        Backend.updateTaskIgnoreFailedValidation(props.taskId, true, oktaAuth);
    };

    const handleKeepProcessing = async () => {
        showProgress(true);
        await Backend.runExistsProcessInject(task_id, oktaAuth);
        showProgress(false);
        props.setIgnoreValidationsErrors(true);
        handleCloseDialog();
    };

    return (
        <DialogCard open={props.showFailedValidationsDialog}>
            <DialogTitle>
                <div className={classes.dialogTitleContainer}>
                    <Typography variant="h3" className={classes.dialogTitle}>
                        Failed Validations
                    </Typography>
                    <img
                        src={AquantCancelIcon}
                        onClick={handleCloseDialog}
                        alt="cancel icon"
                        className={classes.cancelDialogImg}
                    />
                </div>
            </DialogTitle>

            <Divider />

            <DialogContent>
                <DialogContentText className={classes.dialogContentText}>
                    <div className={classes.mainSentence}>
                        The {lowImpactMessages.length + highImpactMessages.length} following validations have stopped
                        the process.
                    </div>
                    <div className={classes.secondarySentence}>
                        To keep processing the data, the associated events will be excluded from the data. Please
                        confirm the next action.
                    </div>
                </DialogContentText>
                <Box className={classes.tabsContainer}>
                    <Tabs
                        TabIndicatorProps={{ style: { backgroundColor: '#2468FF', width: '155px' } }}
                        className={classes.tabs}
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="basic tabs example"
                    >
                        {
                            <Tab
                                icon={<img src={AquantErrorIcon} alt="alert icon" />}
                                iconPosition="start"
                                disabled={highImpactMessages.length === 0}
                                label={
                                    <Typography className={classes.tab}>
                                        High Impact ({highImpactMessages.length})
                                    </Typography>
                                }
                                {...getTabProps(0)}
                            />
                        }
                        {
                            <Tab
                                icon={<img src={AquantAlertIcon} alt="error icon" />}
                                iconPosition="start"
                                disabled={lowImpactMessages.length === 0}
                                label={
                                    <Typography className={classes.tab}>
                                        Low Impact ({lowImpactMessages.length})
                                    </Typography>
                                }
                                {...getTabProps(1)}
                            />
                        }
                    </Tabs>
                    <Button
                        className={classes.downloadBtn}
                        onClick={(event) => {
                            if (props.validationProgress.processorOutputUrl) {
                                props.handleDownload(event, props.validationProgress.processorOutputUrl);
                            }
                        }}
                    >
                        <img src={AquantDownloadIcon} alt="download icon" className={classes.downloadIcon} /> Download
                    </Button>
                </Box>
                <Divider />
                <TabPanel value={tabValue} index={0}>
                    <List className={classes.listContainer}>
                        {highImpactMessages.map((msg, index) => (
                            <ListItem key={index} className={classes.listItem}>
                                {msg.message}
                            </ListItem>
                        ))}
                    </List>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <List className={classes.listContainer}>
                        {lowImpactMessages.map((msg, index) => (
                            <ListItem key={index} className={classes.listItem}>
                                {msg.message}
                            </ListItem>
                        ))}
                    </List>
                </TabPanel>
                <DialogActions style={{ padding: '0px' }}>
                    <Button className={classes.cancelBtn} onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button className={classes.keepProcessingBtn} onClick={handleKeepProcessing}>
                        Keep Processing
                    </Button>
                </DialogActions>
            </DialogContent>
        </DialogCard>
    );
});

export default FailedValidationsDialog;
